import { useState, useEffect } from 'react';
import { InputField, SelectField, RadioField } from '@/components/formParts';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { MdCheck, MdError } from 'react-icons/md';
import { zodResolver } from '@hookform/resolvers/zod';
import { fetchJobOptions, fetchIndustryOptions, fetchPurposeOptions, fetchDenshijitiOptions, genderOptions, MasterOption} from '@/data';

// バリデーション用のスキーマ設定
// === 生年月日セレクト用 追加ユーティリティ ===
const pad2 = (n: number) => String(n).padStart(2, '0');
const daysInMonth = (year: number, month: number) => new Date(year, month, 0).getDate();

const buildYearOptions = () => {
  const now = new Date();
  const current = now.getFullYear();
  const maxAge = 120;
  const years: { value: string; label: string }[] = [];
  for (let y = current; y >= current - maxAge; y--) {
    years.push({ value: String(y), label: `${y}年` }); // ★ここを変更
  }
  return years;
};

const monthOptions = Array.from({ length: 12 }, (_, i) => {
  const m = i + 1;
  return { value: pad2(m), label: `${m}月` }; // ★ここを変更
});
const schemaBase = z.object({
  last_name_kanji: z.string().min(1, { message: "姓（漢字）は必須です" })
    .max(50, { message: "姓（漢字）は50文字以内で入力してください" })
    .regex(/^[\u3005\u3006\u3400-\u4DBF\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF\uF900-\uFAFF　]+$/, { message: "ひらがな、カタカナ、漢字、全角スペース（1つまで）で入力してください" })
    .refine((val) => val.split('　').length <= 2, { message: "全角スペースは1つまでで入力してください" }),
  first_name_kanji: z.string().min(1, { message: "名（漢字）は必須です" })
    .max(50, { message: "名（漢字）は50文字以内で入力してください" })
    .regex(/^[\u3005\u3006\u3400-\u4DBF\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF\uF900-\uFAFF]+$/, { message: "ひらがな、カタカナ、漢字で入力してください" }),
  last_name_alphabet: z.string().min(1, { message: "姓（英字）は必須です" })
    .max(50, { message: "姓（英字）は50文字以内で入力してください" })
    .regex(/^[a-zA-Z\s\-]+$/, { message: "半角英字、スペース、ハイフンのみ入力してください" }),
  first_name_alphabet: z.string().min(1, { message: "名（英字）は必須です" })
    .max(50, { message: "名（英字）は50文字以内で入力してください" })
    .regex(/^[a-zA-Z\s\-]+$/, { message: "半角英字、スペース、ハイフンのみ入力してください" }),
  birthday: z.string().min(1, { message: "生年月日は必須です" }),
  gender: z.string().min(1, { message: "性別は必須です" }),
  job: z.string().min(1, { message: "職業形態は必須です" }),
});

const industrySchema = z.object({
  industry: z.string().min(1, { message: "業種は必須です" }),
  industry_other: z.string().optional(),
}).superRefine((data, ctx) => {
  if (data.industry === '99' && !data.industry_other) {
    ctx.addIssue({
      code: 'custom',
      message: 'その他を選択の場合、記入は必須です',
      path: ['industry_other'],
    });
  }
  if (data.industry === '99' && (data.industry_other ?? '').length > 100) {
    ctx.addIssue({
      code: 'custom',
      message: 'その他の内容は100文字以内で入力してください',
      path: ['industry_other'],
    });
  }
});
const schemaPassword = z.object({
  password: z.string().min(1, { message: "パスワードは必須です" }),
  password_confirm: z.string().min(1, { message: "パスワード確認は必須です" }),
}).superRefine((data, ctx) => {
  const password = data.password;
  // 同一文字連続8回以上禁止
  if (/(.)\1{7,}/.test(password)) {
    ctx.addIssue({
      code: 'custom',
      message: 'パスワードに同一文字を8回以上連続で使用することはできません',
      path: ['password'],
    });
    return;
  }

  if (data.password !== data.password_confirm) {
    ctx.addIssue({
      code: 'custom',
      message: 'パスワードが一致しません',
      path: ['password_confirm'],
    });
  }
});

// const schema = schemaBase.and(industrySchema);
const schema = schemaBase.and(industrySchema).and(schemaPassword)
function defaultBirthday1990Today(): string {
  const now = new Date();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  // 1990年は平年なので 2/29 は 2/28 に丸める
  if (m === '02' && d === '29') return '1990-02-28';
  return `1990-${m}-${d}`;
}

// バリデーション用の型定義
export type RegisterBasicFormData = z.infer<typeof schema>;

// フォームコンポーネント
const RegisterBasicForm = ( { enabled, onValidationChange, shouldSubmit }: {
  enabled: boolean;
  onValidationChange: (data: {isValid: boolean, formData: RegisterBasicFormData | undefined}) => void;
  shouldSubmit: boolean;
}
) => {

  const { register, setValue, handleSubmit, watch, formState: { errors, isValid }, trigger } = useForm<RegisterBasicFormData>({
    mode: 'onTouched',
    resolver: zodResolver(schema),
    defaultValues: {
      birthday: defaultBirthday1990Today(), // ← これを追加
    },
  });
  // === 生年月日セレクト用のローカル状態 ===
  const [birthY, setBirthY] = useState<string>('');
  const [birthM, setBirthM] = useState<string>('');
  const [birthD, setBirthD] = useState<string>('');
  // セレクトの年オプションは固定生成
  const yearOptions = useState(buildYearOptions())[0];  
  const [jobOptions, setJobOptions] = useState<MasterOption[]>([]);
  const [industryOptions, setIndustryOptions] = useState<MasterOption[]>([]);

  const passwordValue = watch('password');
  const passwordEnoughLength = passwordValue?.length >= 8;
  const passwordHasSymbol = passwordValue?.match(/[\W_]/);
  const passwordHasLowercase = passwordValue?.match(/[a-z]/);
  const passwordHasUppercase = passwordValue?.match(/[A-Z]/);
  const passwordHasNumber = passwordValue?.match(/\d/);

  // データ取得
  // watchで保持している birthday(YYYY-MM-DD) から初期化
  useEffect(() => {
  const cur = watch('birthday') || defaultBirthday1990Today(); // 既定: 1990-xx-xx
  const [y, m, d] = cur.split('-');
  setBirthY(y);
  setBirthM(m);
  setBirthD(d);
  }, []);

  // 年/月/日が変わるたびに birthday 文字列(YYYY-MM-DD)へ反映
  useEffect(() => {
  if (!birthY || !birthM) return;

  // 月の最大日数に合わせて日を丸める
  const maxD = daysInMonth(Number(birthY), Number(birthM));
  const safeD = Math.min(Number(birthD || '01'), maxD);
  const dd = pad2(safeD);

  const value = `${birthY}-${birthM}-${dd}`;
  setBirthD(dd);
  setValue('birthday', value, { shouldValidate: true, shouldDirty: true });
  // birthdayのバリデーションを即時トリガ
  trigger('birthday');
  }, [birthY, birthM, birthD]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [job, industry] = await Promise.all([
          fetchJobOptions(),
          fetchIndustryOptions(),
          fetchPurposeOptions(),
          fetchDenshijitiOptions()
        ]);
        setJobOptions(job);
        setIndustryOptions(industry);
      } catch (error) {
        console.error('データ取得エラー:', error);
      }
    };
    loadData();

  }, []);

  // valueの初期化
  useEffect(() => {
    setValue('gender', '');
  }, []);

  // 親にバリデーション状態を通知
  useEffect(() => {
    onValidationChange({isValid, formData: isValid ? watch() : undefined});
  }, [isValid]);

  // 親からのsubmit要求があったらバリデーションを実行
  useEffect(() => {
    if( shouldSubmit ) {
      trigger();
    }
  }, [shouldSubmit]);

  // industry変更時のバリデーション発火
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'industry') {
        if (value.industry !== '99') {
          trigger('industry_other');
        }
      }
    });
    
    return () => subscription.unsubscribe();
  }, [watch, trigger])

  return (
    <>    
      <div className="grid xmd:grid-cols-2 gap-x-4 gap-y-4">
        <InputField label="パスワード" type="password" register={register('password')} errors={errors} hasRequiredLabel={true} onContextMenu={(e) => e.preventDefault()} disabled={!enabled} />
        <InputField label="パスワード確認" type="password" register={register('password_confirm')} errors={errors} hasRequiredLabel={true} onContextMenu={(e) => e.preventDefault()} disabled={!enabled} />
      </div>
      <div className="text-gray-500 mt-2">
        <p><small>※パスワードは8文字以上で英大文字・小文字・数字・記号を含めてください</small></p>
        <ul className="flex flex-col gap-x-4 gap-y-2 mt-2">
          <li className="flex items-center gap-2">
            <span>{passwordEnoughLength ? <MdCheck color="green" /> : <MdError color="gray" />}</span>
            <small className={passwordEnoughLength ? 'text-black' : 'text-red-600'}>8文字以上</small>
          </li>

          <li className="flex items-center gap-2">
            <span>{passwordHasSymbol ? <MdCheck color="green" /> : <MdError color="gray" />}</span>
            <small className={passwordHasSymbol ? 'text-black' : 'text-red-600'}>記号（!@#$%^&*_-）</small>
          </li>

          <li className="flex items-center gap-2">
            <span>{passwordHasLowercase ? <MdCheck color="green" /> : <MdError color="gray" />}</span>
            <small className={passwordHasLowercase ? 'text-black' : 'text-red-600'}>半角英小文字</small>
          </li>

          <li className="flex items-center gap-2">
            <span>{passwordHasUppercase ? <MdCheck color="green" /> : <MdError color="gray" />}</span>
            <small className={passwordHasUppercase ? 'text-black' : 'text-red-600'}>半角英大文字</small>
          </li>

          <li className="flex items-center gap-2">
            <span>{passwordHasNumber ? <MdCheck color="green" /> : <MdError color="gray" />}</span>
            <small className={passwordHasNumber ? 'text-black' : 'text-red-600'}>半角数字</small>
          </li>
        </ul>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <InputField label="姓（漢字）" type="text" register={register('last_name_kanji')} errors={errors} hasRequiredLabel={true} disabled={!enabled} />
        <InputField label="名（漢字）" type="text" register={register('first_name_kanji')} errors={errors} hasRequiredLabel={true} disabled={!enabled} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <InputField label="姓（英字）" type="text" register={register('last_name_alphabet')} errors={errors} hasRequiredLabel={true} disabled={!enabled} />
        <InputField label="名（英字）" type="text" register={register('first_name_alphabet')} errors={errors} hasRequiredLabel={true} disabled={!enabled} />
      </div>
      {/* <div className="xmd:max-w-[40rem]"><InputField label="生年月日" type="date" register={register('birthday')} errors={errors} hasRequiredLabel={true} disabled={!enabled} /></div> */}
      {/* 新：DD / MM / YYYYのセレクト */}
      <div className="xmd:max-w-[40rem]">
        <label className="bxmd:grid-cols-2 gap-x-4 gap-y-4">
          生年月日 <span className="text-red-500">*</span>
        </label>
        <div className="flex gap-2">
          {/* Year */}
          <select
            className="flex-1 rounded-md border border-gray-300 px-3 py-2"
            value={birthY}
            onChange={(e) => setBirthY(e.target.value)}
            disabled={!enabled}
          >
            <option value="" disabled>西暦</option>
            {yearOptions.map((y) => (
              <option key={y.value} value={y.value}>{y.label}</option>
            ))}
          </select>
          {/* Month */}
          <select
            className="flex-1 rounded-md border border-gray-300 px-3 py-2"
            value={birthM}
            onChange={(e) => setBirthM(e.target.value)}
            disabled={!enabled}
          >
            <option value="" disabled>月</option>
            {monthOptions.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
          {/* Day */}
          <select
            className="flex-1 rounded-md border border-gray-300 px-3 py-2"
            value={birthD}
            onChange={(e) => setBirthD(e.target.value)}
            disabled={!enabled}
          >
            <option value="" disabled>日</option>
            {Array.from(
              { length: birthY && birthM ? daysInMonth(Number(birthY), Number(birthM)) : 31 },
              (_, i) => {
                const d = pad2(i + 1);
                const lbl = `${i + 1}日`;         // ★表示だけ “日” 付き・ゼロ詰めなし
                return <option key={d} value={d}>{lbl}</option>;
              }
            )}
          </select>
        </div>

        {/* エラー表示（既存のエラー構造に合わせて） */}
        {errors?.birthday?.message && (
          <p className="mt-1 text-sm text-red-600">{String(errors.birthday.message)}</p>
        )}
      </div>
      <RadioField label="性別" options={genderOptions} register={register('gender')} errors={errors} hasRequiredLabel={true} disabled={!enabled} />
      <SelectField label="職業形態" options={jobOptions} placeholder="選択してください" register={register('job')} errors={errors} hasRequiredLabel={true} disabled={!enabled} />
      <div className="flex flex-col gap-4">
        <SelectField label="職業" options={industryOptions} placeholder="選択してください" register={register('industry')} errors={errors} hasRequiredLabel={true} disabled={!enabled} />
        {watch('industry') === '99' && (
          <InputField placeholder="その他の場合" type="text" register={register('industry_other')} errors={errors} hasRequiredLabel={true} disabled={!enabled} />
        )}
      </div>
    </>
  );
}

export default RegisterBasicForm;
